export const en = {
  common: {
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    create: "Create",
    update: "Update",
    loading: "Loading",
    error: "Error",
    saved: "Saved",
    yes: "Yes",
    no: "No",
    all: "All",
    apply: "Apply",
    signOut: "Sign out",
    confirm: "Confirm",
    optionalNotes: "Optional notes",
    confirmDelete: "Confirm delete",
    current: "Current",
    ok: "OK",
    warning: "Warning",
    success: "Success",
    back: "Back",
    copied: "Copied",
    refresh: "Refresh",
    add: "Add",
    change: "Change",
    countryCode: "Country code",
    none: "None",
  },

  auth: {
    login: "Login",
    logout: "Logout",
    register: "Register",
    email: "Email",
    phone: "Phone",
    password: "Password",
    confirmPassword: "Confirm password",
    firstName: "First name",
    lastName: "Last name",
    accountName: "Account name",
    loginFailed: "Login failed.",
    signInSubtitle: "Manage your apartments, bookings and guests.",
    forgotPassword: "Forgot password?",
    forgotPasswordSubtitle:
      "Enter your email and we will send you password reset instructions.",
    sendResetLink: "Send reset link",
    backToLogin: "Back to login",
    or: "or",
    loginWithGoogle: "Continue with Google",
    loginWithFacebook: "Continue with Facebook",
    noAccount: "Don't have an account?",
    createAccount: "Create account",
    alreadyHaveAccount: "Already have an account?",
    registerSubtitle:
      "Create your RentPilot account and start managing rentals.",
    verifyEmailBanner:
      "Please verify your email address before using all RentPilot features.",
    resendVerificationEmail: "Resend email",
    iVerified: "I verified",
    resetPassword: "Reset password",
    resetPasswordSubtitle: "Enter a new password for your RentPilot account.",
    newPassword: "New password",
    invalidResetLink: "The password reset link is invalid or incomplete.",
  },

  tabs: {
    calendar: "Calendar",
    properties: "Properties",
    statistics: "Statistics",
    profile: "Profile",
    team: "Team",
    support: "Support",
    cleaningTasks: "Tasks",
    invoices: "Invoices",
    menu: "Menu",
  },

  cleaners: {
    title: "Team",
    add: "Add",
    cleaners: "Team members",
    newCleaner: "New team member",
    createCleaner: "Create team member",
    noCleaners: "No team members yet.",
    schedule: "Schedule",
    noTasks: "No cleaning tasks.",
    assignCleaner: "Assign team member",
    cleanedHistory: "Cleaned history",
    noCleanedHistory: "No cleaned units yet.",
    failedCreateCleaner: "Failed to create team member.",
    deleteQuestion: "Delete {{name}}?",
    description: "Manage team members and their access to the application.",
    teamAccess: "Team access",
    members: "members",
    addMemberDescription:
      "Add a manager, viewer or housekeeper to your account.",
    roleOverview: "Role overview",
    managerDescription:
      "Can manage bookings, properties, guests, tasks and team members.",
    viewerDescription: "Can view the calendar without managing data.",
    cleanerDescription: "Can only see assigned cleaning tasks.",
  },

  cleaning: {
    myTasks: "My tasks",
    today: "Today",
    upcoming: "Upcoming",
    done: "Done",
    noTasksToday: "No tasks for today.",
    noUpcomingTasks: "No upcoming tasks.",
    noDoneTasks: "No completed tasks.",
    scheduled: "Scheduled",
    cleanedAt: "Cleaned at",
    assignedTo: "Assigned to",
    unassigned: "Unassigned",
    start: "Start",
    markCleaned: "Mark cleaned",
    futureTaskCannotBeCompleted:
      "This task can be completed on its scheduled day.",
    status: {
      pending: "Pending",
      in_progress: "In progress",
      done: "Done",
      cancelled: "Cancelled",
    },
    description:
      "View today’s tasks, upcoming cleaning tasks, and completed cleaning history.",
  },

  calendar: {
    unit: "Unit",
    noUnits: "No units",
    filters: "Filters",
    startDate: "Start date",
    endDate: "End date",
    thisWeek: "This week",
    twoWeeks: "Two weeks",
    threeWeeks: "Three weeks",
    thisMonth: "This month",
    occupied: "Occupied",
    unavailable: "Unavailable",
    rangeOverlaps: "Selected date range overlaps with an existing booking.",
    occupiedCellMessage:
      "This unit already has a booking on the selected date.",
    selectCheckIn: "Select check-in",
    selectCheckOut: "Select check-out",
    clear: "Clear",
    multi: "Multi",
    single: "Single",
    next7Days: "Next 7 days",
    next14Days: "Next 14 days",
    selectedStart: "Selected start",
    tapCheckoutDate: "Tap checkout date to create a booking",
    subtitle:
      "Overview of units, bookings, payments and cleaning-triggered stays.",
    available: "Available",
    addObjects: "Add objects",
    addObjectsDescription:
      "Create your first property and unit to start managing reservations.",
    month: "Month",
    next: "Next",
    allUnits: "All apartments",
    checkInAfter: "Check-in after 14:00",
    checkOutBefore: "Check-out until 10:00",
    cleaning: "Cleaning",
    private: "Private",
    closed: "Closed",
    today: "Today",
    fullYear: "Full year",
    availableDates: "Available dates",
    availableDatesByUnit: "Available dates by apartment",
    shown: "shown",
    terms: "dates",
    noFreePeriods: "No available dates",
  },

  booking: {
    newBooking: "New booking",
    bookingDetails: "Booking details",
    guestFirstName: "Guest first name",
    guestLastName: "Guest last name",
    guest: "Guest",
    phone: "Phone",
    email: "Email",
    unit: "Unit",
    checkIn: "Check-in",
    checkOut: "Check-out",
    nights: "Nights",
    pricePerNight: "Price per night",
    total: "Total",
    status: "Status",
    payment: "Payment",
    paymentStatus: "Payment status",
    unpaid: "Unpaid",
    deposit: "Deposit",
    paid: "Paid",
    reserved: "Reserved",
    checkedIn: "Checked in",
    checkedOut: "Checked out",
    cancelled: "Cancelled",
    notes: "Notes",
    createBooking: "Create booking",
    failedCreate: "Failed to create booking.",
    guestFirstNamePlaceholder: "John",
    guestLastNamePlaceholder: "Smith",
    phonePlaceholder: "+387...",
    emailPlaceholder: "john@example.com",
    pricePlaceholder: "45",
    arrivingAround: "Arriving around 18:00",
    currency: "Currency",
    dateRange: "Stay dates",
    selectDates: "Select dates",
    selectCheckIn: "Select check-in date",
    selectedDateOccupied: "This date is already occupied.",
    selectedRangeOverlaps: "Selected dates overlap with an existing booking.",
    numberOfGuests: "Guests",
    checkoutAfterCheckin: "Check-out must be after check-in.",
    contactGuest: "Contact guest",
    invoice: "Invoice",
    whatsapp: "WhatsApp",
    missingEmail: "This guest does not have an email address.",
    missingPhone: "This guest does not have a phone number.",
    noContactInfo: "This guest has no email address or phone number.",
    confirmSendInvoice: "Send invoice to this guest?",
    invoiceSent: "Invoice sent.",
    failedSendInvoice: "Failed to send invoice.",
    booking: "Booking",
    editBooking: "Edit booking",
    saveBooking: "Save booking",
    sendInvoice: "Send invoice",
    cancelBooking: "Cancel booking",
    cancelBookingQuestion: "Are you sure you want to cancel this booking?",
    bookingCreated: "Booking created.",
    bookingUpdated: "Booking updated.",
    bookingCancelled: "Booking cancelled.",
    failedUpdate: "Failed to update booking.",
    failedCancel: "Failed to cancel booking.",
    unitMissing: "Unit is missing.",
    emailUnavailable: "Email is unavailable.",
    emailSubject: "Reservation information",
    emailBody: "Hello, here are your reservation details.",
    whatsappMessage: "Hello, here are your reservation details.",
    missingGuestEmail: "Guest email is missing.",
    confirmSendInvoiceMessage:
      "This will send the invoice to the guest email address.",
    checked_in: "Checked in",
    checked_out: "Checked out",
  },

  bookingMessages: {
    title: "WhatsApp message",
    templateOne:
      "Hello {{guest}}, your reservation for {{unit}} is confirmed from {{checkIn}} to {{checkOut}}.",
    templateTwo:
      "Hello {{guest}}, thank you for your reservation. Check-in is on {{checkIn}} and check-out is on {{checkOut}}.",
    templateThree:
      "Hello {{guest}}, we are looking forward to your stay at {{unit}}. Your dates are {{checkIn}} - {{checkOut}}.",
    templateButton: "Template {{number}}",
    customPlaceholder: "Write your message...",
    openWhatsApp: "Open WhatsApp",
    customMessage: "Custom message",
    invalidPhone: "Invalid phone number.",
    missingPhone: "Phone number is missing.",
    whatsappUnavailable: "WhatsApp is unavailable.",
  },

  properties: {
    title: "Properties",
    newProperty: "New property",
    createProperty: "Create property",
    propertyName: "Property name",
    address: "Address",
    city: "City",
    notes: "Notes",
    noUnits: "No units yet",
    units: "units",
    newUnit: "New unit",
    createUnit: "Create unit",
    unitName: "Unit name",
    type: "Type",
    capacity: "Capacity",
    defaultPricePerNight: "Default price per night",
    noDefaultPrice: "No default price",
    guests: "guests",
    disable: "Disable",
    enable: "Enable",
    deleteProperty: "Delete property",
    deletePropertyQuestion:
      "Delete {{name}}? This only works if it has no booking history.",
    deleteUnit: "Delete unit",
    deleteUnitQuestion:
      "Delete this unit? If it has booking history, you may deactivate it instead.",
    failedCreateProperty: "Failed to create property.",
    failedCreateUnit: "Failed to create unit.",
    apartment: "Apartment",
    room: "Room",
    studio: "Studio",
    house: "House",
    other: "Other",
    newPropertyPlaceholder: "Sarajevo Apartments",
    addressPlaceholder: "Street 1",
    cityPlaceholder: "Sarajevo",
    notesPlaceholder: "Optional notes",
    unitNamePlaceholder: "Apartment A",
    pricePlaceholder: "45",
    capacityPlaceholder: "2",
    pricePerNightShort: "{{price}} / night",
    deletePropertySuccess: "Property deleted.",
    deleteUnitSuccess: "Unit deleted.",
    addUnit: "Add unit",
    activeUnits: "active units",
    inactiveUnits: "inactive units",
    calendarSync: "Calendar sync",
    calendarSyncHelp:
      "Paste Airbnb and Booking.com iCal export links here. RentPilot will import those calendars and block matching dates.",
    airbnbIcalLink: "Airbnb iCal link",
    bookingIcalLink: "Booking.com iCal link",
    rentPilotExportLink: "RentPilot export link",
    rentPilotExportCalendar: "RentPilot export calendar",
    rentPilotExportHelp:
      "Copy this link into Airbnb and Booking.com so they can block dates from RentPilot.",
    rentPilotExportHelpLong:
      "Paste this link into Airbnb or Booking.com external calendar settings so those platforms can block RentPilot dates.",
    airbnbConnected: "Airbnb connected",
    airbnbNotConnected: "Airbnb not connected",
    bookingConnected: "Booking connected",
    bookingNotConnected: "Booking not connected",
    noExternalCalendarsConnected: "No external calendars connected.",
    copyShareExportLink: "Copy export link",
    exportLinkMissing:
      "Export link is missing. Backend did not return calendar_export_url for this unit.",
    exportLinkCopied: "Export link copied.",
    editUnit: "Edit unit",
    saveUnit: "Save unit",
    noUnitsDescription:
      "Add rooms/apartments so bookings and cleaning tasks can be assigned to this property.",
    activateUnit: "Activate unit",
    deactivateUnit: "Deactivate unit",
    failedDeleteProperty: "Failed to delete property.",
    failedDeleteUnit: "Failed to delete unit.",
    failedUpdateUnit: "Failed to update unit.",
  },

  statistics: {
    title: "Statistics",
    revenue: "Revenue",
    bookings: "Bookings",
    nights: "Nights",
    averageBooking: "Average booking",
    breakdown: "Breakdown",
    noStatistics: "No statistics for selected filters.",
    groupBy: "Group by",
    unit: "Unit",
    property: "Property",
    month: "Month",
    payment: "Payment",
    sevenDays: "7 days",
    fourteenDays: "14 days",
    thirtyDays: "30 days",
    thisMonth: "This month",
    allProperties: "All properties",
    allUnits: "All units",
    byUnit: "By unit",
    byProperty: "By property",
    byMonth: "By month",
    netProfit: "Net profit",
    occupancy: "Occupancy",
    profitMargin: "Profit margin",
    expenseRatio: "Expense ratio",
    revenueByUnit: "Revenue by unit",
    revenueByApartment: "Revenue by apartment",
    noRevenueForPeriod: "No revenue for selected period.",
    noExpensesForPeriod: "No expenses for selected period.",
    manualVsRecurringExpenses: "Manual vs recurring expenses",
    cashflow: "Cashflow",
    latestNetProfit: "Latest net profit: {{amount}}",
    performanceBreakdown: "Performance breakdown",
    breakdownMeta: "{{bookings}} bookings · {{nights}} nights",
    margin: "Margin",
    revenueExpensesHint: "{{revenue}} revenue · {{expenses}} expenses",
    expenses: "Expenses",
    expensesByCategory: "Expenses by category",
    filters: "Filters",
  },

  expenses: {
    expenses: "Expenses",
    newExpense: "New expense",
    editExpense: "Edit expense",
    newRecurringExpense: "New recurring expense",
    editRecurringExpense: "Edit recurring expense",
    manualExpenses: "Manual expenses",
    recurringExpenses: "Recurring expenses",
    totalExpenses: "Total expenses",
    noExpenses: "No expenses yet.",
    noRecurringExpenses: "No recurring expenses yet.",
    name: "Name",
    category: "Category",
    categoryPlaceholder: "Cleaning, utilities, maintenance...",
    amount: "Amount",
    date: "Date",
    frequency: "Frequency",
    monthly: "Monthly",
    yearly: "Yearly",
    interval: "Interval",
    dayOfMonth: "Day of month",
    startDate: "Start date",
    endDate: "End date",
    active: "Active",
    inactive: "Inactive",
    noProperty: "No property",
    noUnit: "No unit",
    uncategorized: "Uncategorized",
    expensesByCategory: "Expenses by category",
    expensesByType: "Expenses by type",
    expensesByUnit: "Expenses by unit",
    addExpense: "Add expense",
    oneTimeExpense: "One-time expense",
    oneTimeExpenseDescription:
      "Use this for a single cost, like a repair, towels, or one purchase.",
    recurringExpense: "Recurring expense",
    recurringExpenseDescription:
      "Use this for repeated costs, like internet, subscriptions, or monthly supplies.",
  },

  profile: {
    title: "Profile",
    firstName: "First name",
    lastName: "Last name",
    phone: "Phone",
    saveProfile: "Save profile",
    profileUpdated: "Profile updated.",
    updateEmail: "Update email",
    currentPassword: "Current password",
    emailUpdated: "Email updated.",
    password: "Password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    updatePassword: "Update password",
    passwordUpdated: "Password updated.",
    passwordsDoNotMatch: "New password and confirmation do not match.",
    language: "Language",
    signOutQuestion: "Are you sure you want to sign out?",
    failedProfileUpdate: "Failed to update profile.",
    failedEmailUpdate: "Failed to update email.",
    failedPasswordUpdate: "Failed to update password.",
    email: "Email",
    emailPlaceholder: "email@example.com",
    phonePlaceholder: "+387...",
    firstNamePlaceholder: "First name",
    lastNamePlaceholder: "Last name",
    currentPasswordPlaceholder: "Current password",
    newPasswordPlaceholder: "New password",
    confirmNewPasswordPlaceholder: "Confirm new password",
    signOut: "Sign out",
    currencySettings: "Currency",
  },

  currency: {
    currency: "Currency",
    currencyUpdated: "Currency updated",
    defaultCurrency: "Default currency",
    bam: "KM",
    eur: "€",
    usd: "$",
    rsd: "RSD",
    failedCurrencyUpdate: "Failed to update currency.",
  },
  support: {
    title: "Support",
    needSupport: "Need support?",
    description:
      "Contact us if you need help with your account, bookings, properties, cleaners, or subscription.",
    callSupport: "Call support",
    emailSupport: "Email support",
  },

  subscription: {
    title: "Subscription",
    subtitle: "Choose the plan that matches your rental setup.",
    currentPlan: "Current plan",
    freeNotice: "You are currently using the free plan.",
    perMonth: "/ month",
    choosePlan: "Choose plan",
    manageBilling: "Manage billing",
    upgradeRequired: "Upgrade required",
    upgradeRequiredMessage:
      "Your current plan limit has been reached. Please upgrade your subscription.",
    accessWarningTitle: "RentPilot access expires soon",

    accessWarningMessage:
      "Your RentPilot access expires on {{date}}. Choose or renew a plan before that date to continue creating and changing data.",

    customAccessWarningMessage:
      "Your custom RentPilot plan expires on {{date}}. Contact RentPilot support and arrange payment before that date to continue using the app.",

    accessExpiredTitle: "RentPilot access is restricted",

    accessExpiredMessage:
      "Your subscription has expired or has not been paid. You can still view existing data, but creating or changing data is disabled until payment is completed.",

    customAccessExpiredMessage:
      "Your custom RentPilot plan has expired or has not been paid. You can still view existing data, but creating or changing data is disabled. Contact RentPilot support to arrange payment and restore access.",

    contactAccountOwnerMessage:
      "Contact the account owner to arrange payment and restore full access.",

    viewPlansCta: "View plans",

    contactSupportCta: "Contact support",

    noticeCloseCta: "Understood",
    billingPeriod: "billing period",
    trialExpiresSoonBanner:
      "Your trial expires in {{hours}} hours. To continue using RentPilot, choose a plan in the web app.",
    trialExpiresSoonCta: "Choose plan",
    billingPeriodDescription: "Choose how many months you want to pay upfront.",
    planColumn: "Plan",
    limitColumn: "Limit",
    priceColumn: "Price",
    discountColumn: "Discount",
    featuresColumn: "Included",
    actionColumn: "Action",
    recommended: "Recommended",
    discountBadge: "{{discount}}% off",
  },

  menu: {
    pricing: "Pricing",
    pricingDescription: "View your current plan and billing options.",
    currentPlan: "Current plan",
    pricingComingSoon: "Full pricing management will be connected here.",
    myAccount: "My Account",
    myAccountDescription: "Profile, email and password.",
    support: "Support",
    supportDescription: "Contact support or view help information.",
    settings: "Settings",
    settingsDescription: "App appearance, language, and currency",
    privacy: "Privacy & company information",
    privacyDescription: "Company, customer service, and privacy information.",
  },

  settings: {
    appAppearance: "App Appearance",
    fontSize: "Font Size",
    fontSmall: "Small",
    fontMedium: "Medium",
    fontLarge: "Large",
    fontVeryLarge: "Very Large",
    bookingColors: "Booking Colors",
    theme: "Theme",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
  },
  roles: {
    role: "Role",
    owner: "Owner",
    manager: "Manager",
    cleaner: "Housekeeper",
    viewer: "Viewer",
    teamMember: "Team member",
  },

  invoices: {
    title: "Sent invoices",
    description: "View sent invoices and invoice email history.",
    emptyTitle: "No invoices yet",
    empty: "No invoices have been sent yet.",
    failedLoad: "Failed to load invoices.",
    sentAt: "Sent at",
    status: {
      pending: "Pending",
      sent: "Sent",
      failed: "Failed",
    },
  },

  accountDeletion: {
    title: "Delete account",
    description:
      "This will request permanent deletion of your account. All properties, units, bookings, guests, team members, invoices, cleaning tasks, expenses and settings connected to this account will be deleted.",
    warning:
      "This action is scheduled with a safety delay, but you should only use it if you are sure.",
    typeDelete: "Type DELETE to confirm account deletion.",
    requestDeletion: "Request deletion",
    alreadyScheduled: "Account deletion is already scheduled for {{date}}.",
    cancelInfo:
      "You can cancel the deletion request before the scheduled deletion date.",
    cancelDeletion: "Cancel deletion request",
    cancelQuestion: "Are you sure you want to cancel the deletion request?",
    scheduledMessage: "Account deletion has been scheduled.",
    cancelledMessage: "Account deletion request has been cancelled.",
    failedRequest: "Failed to request account deletion.",
    failedCancel: "Failed to cancel account deletion.",
  },

  merchant: {
    title: "Merchant information",
    description:
      "Details of the legal entity providing and charging for the RentPilot service.",
    legalName: "Full legal entity name",
    address: "Address",
    businessActivity: "Business activity",
    activityCode: "Activity code",
    registrationNumber: "Registration number",
    taxNumber: "Tax / JIB number",
    website: "Website",
    phone: "Contact phone",
    email: "Contact email",

    customerServiceTitle: "Customer service",
    customerServiceDescription:
      "For information about orders and payments, complaints, service cancellation, and other enquiries, you can contact us using the details below.",

    privacyTitle: "Privacy protection",
    privacyDescription:
      "RentPilot collects only the data necessary to provide the service, manage user accounts, process orders and payments, provide customer support, and comply with legal obligations. Personal data is not sold. Data is available only to authorised persons and service providers who require it to perform their obligations. Card details are entered exclusively on the payment processor's secure page and are neither accessible to nor stored by the RentPilot system.",
  },
};
